import {
  HomeyAPI,
  type Device,
  type HomeyAPIV2,
  type HomeyAPIV3Local,
  type HomeyAPIV3Cloud,
  type ItemProps,
} from 'homey-api';

import { HomeyApiKind } from '../../lib/HomeyApiKind';
import { BaseApiStore, type FetchArgs, type ReconnectArgs } from '../BaseApiStore.ts';

export type DeviceProps = ItemProps<Device>;
export type V3Device =
  | HomeyAPIV3Local.ManagerDevices.Device
  | HomeyAPIV3Cloud.ManagerDevices.Device;
export type V3DeviceProps = ItemProps<V3Device>;

export type DevicesData = { [key: string]: Device };
export type DevicesById = {
  [key: string]: DeviceProps;
};

type PlatformDevicesState<Kind extends HomeyApiKind, Item extends Device> = {
  apiKind: Kind;
  data: Record<string, Item> | null;
  byId: Record<string, ItemProps<Item>> | null;
  isLoading: boolean;
  error: unknown;
};

export type DevicesStoreState =
  | PlatformDevicesState<typeof HomeyApiKind.V2, HomeyAPIV2.ManagerDevices.Device>
  | PlatformDevicesState<typeof HomeyApiKind.V3_LOCAL, HomeyAPIV3Local.ManagerDevices.Device>
  | PlatformDevicesState<typeof HomeyApiKind.V3_CLOUD, HomeyAPIV3Cloud.ManagerDevices.Device>
  | {
      apiKind: null;
      data: null;
      byId: null;
      isLoading: boolean;
      error: unknown;
    };

export class DevicesStore extends BaseApiStore<DevicesStoreState, Device> {
  constructor() {
    super('devices');
  }

  override createInitialState(): DevicesStoreState {
    return DevicesStore.createEmptyState(this.api);
  }

  static createEmptyState(api: HomeyAPI | null): DevicesStoreState {
    const empty = { data: null, byId: null, isLoading: true, error: null };

    if (api == null) {
      return { ...empty, apiKind: null };
    }

    if (HomeyAPI.isHomeyAPIV2(api)) {
      return { ...empty, apiKind: HomeyApiKind.V2 };
    }

    if (HomeyAPI.isHomeyAPIV3Local(api)) {
      return { ...empty, apiKind: HomeyApiKind.V3_LOCAL };
    }

    if (HomeyAPI.isHomeyAPIV3Cloud(api)) {
      return { ...empty, apiKind: HomeyApiKind.V3_CLOUD };
    }

    throw new Error('Unsupported Homey API for DevicesStore');
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(DevicesStore.createEmptyState(api));
    }

    try {
      await this.connectManager({ manager: api.devices });

      if (abortController.signal.aborted) {
        return;
      }

      const state = await DevicesStore.fetchState(api, cache);

      if (abortController.signal.aborted) {
        return;
      }

      this.set(state);
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  static async fetchState(api: HomeyAPI, cache: boolean): Promise<DevicesStoreState> {
    if (HomeyAPI.isHomeyAPIV2(api)) {
      const devicesData = await api.devices.getDevices({ $cache: cache });
      const data = { ...devicesData };
      return {
        apiKind: HomeyApiKind.V2,
        data,
        byId: DevicesStore.getSnapshots(data),
        isLoading: false,
        error: null,
      };
    }

    if (HomeyAPI.isHomeyAPIV3Local(api)) {
      const devicesData = await api.devices.getDevices({ $cache: cache });
      const data = { ...devicesData };
      return {
        apiKind: HomeyApiKind.V3_LOCAL,
        data,
        byId: DevicesStore.getSnapshots(data),
        isLoading: false,
        error: null,
      };
    }

    if (HomeyAPI.isHomeyAPIV3Cloud(api)) {
      const devicesData = await api.devices.getDevices({ $cache: cache });
      const data = { ...devicesData };
      return {
        apiKind: HomeyApiKind.V3_CLOUD,
        data,
        byId: DevicesStore.getSnapshots(data),
        isLoading: false,
        error: null,
      };
    }

    throw new Error('Unsupported Homey API for DevicesStore');
  }

  static getSnapshots<Props>(
    data: Readonly<Record<string, { getProps(): Props }>>,
  ): Record<string, Props> {
    const byId: Record<string, Props> = {};

    for (const [id, device] of Object.entries(data)) {
      byId[id] = device.getProps();
    }

    return byId;
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);
  }

  override shouldRefreshAfterReconnect({ api, disconnectDurationMs }: ReconnectArgs): boolean {
    return true;
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.devices.on('device.create', this.handleCreate);
    api.devices.on('device.update', this.handleUpdate);
    api.devices.on('device.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.devices.off('device.create', this.handleCreate);
    api.devices.off('device.update', this.handleUpdate);
    api.devices.off('device.delete', this.handleDelete);
  }

  getInstanceFromCache(device: { id: string }) {
    return this.getCache()[device.id];
  }

  getCache(): Readonly<DevicesData> {
    const api = this.getApi();

    return api.devices.getCachedItems('device');
  }

  handleCreate = (createdDevice: Device) => {
    this.DEBUG && console.info(`create:device:${createdDevice.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.info('create:device:missing-data');
      return;
    }

    const deviceInstance = this.getInstanceFromCache(createdDevice);

    if (deviceInstance == null) {
      return;
    }

    const deviceReference = deviceInstance.getProps();

    const nextState = { ...state };
    nextState.data = { ...state.data };
    nextState.byId = { ...state.byId };
    nextState.data[createdDevice.id] = deviceInstance;
    nextState.byId[createdDevice.id] = deviceReference;
    this.set(nextState);
  };

  handleUpdate = (updatedDevice: Device) => {
    this.DEBUG && console.info(`update:device:${updatedDevice.id}`);

    this.addToQueue(updatedDevice.id, updatedDevice);

    this.batch((updateQueue) => {
      const state = this.get();

      if (state.data == null) {
        this.DEBUG && console.info('update:device:missing-data');
        return;
      }

      const nextState = { ...state };
      nextState.byId = { ...state.byId };

      let updateCount = 0;

      for (const [id, queuedDevice] of Object.entries(updateQueue)) {
        const deviceInstance = this.getInstanceFromCache(queuedDevice);

        if (deviceInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:device:unknown:${id}`);
          continue;
        }

        const deviceReference = deviceInstance.getProps();

        nextState.byId[id] = deviceReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:device:batch:count:${updateCount}`);

      if (updateCount > 0) {
        this.set(nextState);
      }
    });
  };

  handleDelete = (deletedDevice: Pick<Device, 'id'>) => {
    this.DEBUG && console.info(`delete:device:${deletedDevice.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.log(`delete:device:missing-data`);
      return;
    }

    const deviceInstance = state.data[deletedDevice.id];

    if (deviceInstance == null) {
      return;
    }

    const nextData = { ...state.data };
    const nextById: NonNullable<DevicesStoreState['byId']> = { ...state.byId };

    delete nextData[deviceInstance.id];
    delete nextById[deviceInstance.id];

    const nextState = { ...state };
    nextState.data = nextData;
    nextState.byId = nextById;
    this.set(nextState);
  };

  restore() {
    const api = this.getApi();
    const { isLoading, error } = this.get();

    if (HomeyAPI.isHomeyAPIV2(api)) {
      const data = { ...api.devices.getCachedItems('device') };
      this.set({
        apiKind: HomeyApiKind.V2,
        data,
        byId: DevicesStore.getSnapshots(data),
        isLoading,
        error,
      });
      return;
    }

    if (HomeyAPI.isHomeyAPIV3Local(api)) {
      const data = { ...api.devices.getCachedItems('device') };
      this.set({
        apiKind: HomeyApiKind.V3_LOCAL,
        data,
        byId: DevicesStore.getSnapshots(data),
        isLoading,
        error,
      });
      return;
    }

    if (HomeyAPI.isHomeyAPIV3Cloud(api)) {
      const data = { ...api.devices.getCachedItems('device') };
      this.set({
        apiKind: HomeyApiKind.V3_CLOUD,
        data,
        byId: DevicesStore.getSnapshots(data),
        isLoading,
        error,
      });
      return;
    }

    throw new Error('Unsupported Homey API for DevicesStore');
  }

  updateOneOptimistic(id: string, update: Partial<DeviceProps>) {
    const state = this.get();

    if (state.byId?.[id] == null) {
      return;
    }

    const deviceReference = Object.assign({}, state.byId[id], update);

    const nextState = { ...state };
    nextState.byId = { ...state.byId };
    nextState.byId[id] = deviceReference;
    this.set(nextState);
  }

  restoreOne(id: string) {
    const state = this.get();
    const deviceInstance = this.getInstanceFromCache({ id });

    if (state.byId == null || deviceInstance == null) {
      return;
    }

    const nextState = { ...state };
    nextState.byId = { ...state.byId };
    nextState.byId[id] = deviceInstance.getProps();
    this.set(nextState);
  }
}

export const devicesStore = new DevicesStore();
